import React, { useCallback } from 'react';
import { ColumnInfo, ColumnType } from '@/Types';
import { Column, HeaderFilter, IColumnProps } from 'devextreme-react/data-grid';
import {
  getCalculateDisplayValue,
  getColumnRender,
  getTypeByColumnInfo,
  calculateFilterExpression,
  getFilterValues,
} from '../services/utils';

export const useColumns = <TData extends object>(data: TData[], columnInfos?: ColumnInfo<TData>[]) => {
  const customizeText = (columnType?: ColumnType) => (cellInfo: any) => {
    if (columnType == ColumnType.Number) {
      return cellInfo.valueText;
    }

    if (cellInfo.target == 'headerFilter' && cellInfo.groupInterval)
      return cellInfo.groupInterval == 'month'
        ? cellInfo.valueText
        : `${cellInfo.valueText} (${cellInfo.groupInterval})`;

    if (columnType !== undefined) return getColumnRender(columnType, cellInfo.value);
  };

  const cellRender = (render: ((rowData: TData, rowIndex: number) => React.ReactNode) | undefined) => {
    return render ? (params: { data: TData; rowIndex: number }) => render(params.data, params.rowIndex) : undefined;
  };

  const generateColumns = useCallback(
    (columnInfos?: ColumnInfo<TData>[]) => {
      return columnInfos?.map(
        ({
          render,
          columnType,
          children,
          fieldName,
          headerText,
          visible,
          defaultVisible,
          alignment,
          width,
          minWidth,
          format,
          calculateCellValue,
          allowEditing = false,
          allowFiltering = true,
          allowSorting = true,
          fixedPosition,
        }) => {
          if (children?.length) {
            return (
              <Column key={`${fieldName as string}`} caption={headerText}>
                {generateColumns(children)}
              </Column>
            );
          } // if

          const props: IColumnProps & { key: string } = {
            allowFiltering,
            allowSorting,
            key: `${fieldName as string}`,
            dataField: fieldName,
            caption: headerText,
            format,
            width,
            minWidth,
            alignment:
              columnType &&
              [ColumnType.Number, ColumnType.Decimal, ColumnType.Weight, ColumnType.Currency].includes(columnType)
                ? 'right'
                : alignment,
            dataType: getTypeByColumnInfo(columnType),
            cellRender: cellRender(render),
            customizeText: customizeText(columnType),
            allowEditing,
            calculateDisplayValue: (row: TData) =>
              calculateCellValue?.(row) ?? getCalculateDisplayValue(row, columnType, fieldName),
            fixed: Boolean(fixedPosition),
            fixedPosition,
            calculateFilterExpression: calculateFilterExpression(columnType, fieldName),
          };

          if (defaultVisible !== undefined) {
            props.defaultVisible = defaultVisible;
          } else {
            props.visible = visible;
          }

          return (
            <Column {...props} key={`${fieldName as string}`}>
              <HeaderFilter dataSource={getFilterValues(data, fieldName, columnType)} />
            </Column>
          );
        },
      );
    },
    [data],
  );

  return generateColumns(columnInfos);
};
